import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "src/redux/store";

import { useStoreState } from "src/pages/contentMap";
import { guid } from "src/services/util";
import { useHubAdoption } from "src/redux/actions/adoption/hub";


import { Asset } from "src/types/assets";
import { Stream } from "src/types/streams";
import { Collection } from "src/types/collections";

import Slider from "src/components/slider";
import Wizard from "src/components/wizard";
import Button from "src/components/button";
import Layout, { defaultRow } from "./layout";
import SelectContent, { EntityTreeHandler } from "./selectContent";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
import { SelectEntity } from "./selectEntity";

import { fetchHubLayout, updateHubLayout, updateHubSection } from "src/redux/actions/hub";
import { HubLayout, HubLayoutColumn } from "src/types/hub";

import _ from "lodash";
import cx from "classnames";
import styles from "./editHub.module.scss";
import { EntityType, LayoutPalette } from "src/types/adoption/hub";

const Peek = '/images/peek.svg';

interface EditHubProps {
  activeIndex: number;
  hubLayout: HubLayout;
  createSection: boolean;
  hideEditSection: () => void;
}

interface SelectedEntityType {
  [index: string]: Asset[] | Stream[] | Collection[];
}

interface EditHubContextProps {
  rowItem: HubLayoutColumn[];
  persistedRowItem: HubLayoutColumn[];
  updateRowItem: (field, value) => void;
  activeColumnIndex: number;
  setActiveColumnIndex: (value: number) => void;
  persistedSelectedEntities: SelectedEntityType;
  selectedEntity: SelectedEntityType;
  setSelectedEntity: (value: SelectedEntityType) => void;
  showEntityTree: boolean;
  setShowEntityTree: (value: boolean) => void;
  createSection: boolean;
}

export const EdithubContext = React.createContext<EditHubContextProps>({
  rowItem: [],
  persistedRowItem: [],
  updateRowItem: () => { },
  activeColumnIndex: 0,
  persistedSelectedEntities: {},
  setActiveColumnIndex: () => { },
  selectedEntity: {},
  setSelectedEntity: () => { },
  showEntityTree: false,
  setShowEntityTree: () => { },
  createSection: false
});

const EditHub: React.FC<EditHubProps> = ({
  activeIndex,
  hubLayout,
  createSection,
  hideEditSection
}) => {

  const dispatch = useDispatch();
  const entityTreeCancelRef = useRef<EntityTreeHandler>(null);

  const sections = useSelector((state: RootState) => state.hub?.sections);

  const [rowItem, setRowItem] = useState<HubLayoutColumn[]>([]);
  const [persistedRowItem, setPersistedRowItem] = useState<HubLayoutColumn[]>([]);

  const [selectedEntity, setSelectedEntity] = useState<SelectedEntityType>({});
  const [persistedSelectedEntities, setPersistedSelectedEntities] = useState<SelectedEntityType>({});

  const [activeColumnIndex, setActiveColumnIndex] = useState(0);
  const [isRowItemUpdating, setIsRowItemUpdating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showEntityTree, setShowEntityTree] = useState(false);
  const [isTypeSelectionMode, setIsTypeSelectionMode] = useState(false);
  const [layoutPalette, setLayoutPalette] = useState<LayoutPalette>();
  const [showConfirmSlider, setShowConfirmSlider] = useState(false);

  const { loading: isHubSectionUpdating, error: hubSectionError, succeeded: isHubSectionUpdated, item: updatedHubSection } = useStoreState("hub.item.update", false);
  const { loading: isHubUpdating, error: hubError, succeeded: isHubUpdated } = useStoreState("hub.update", false);
  const { trackSectionCreateLayoutScreenView, trackSectionSaveClickedEvent, trackSectionSaveConfirmClickedEvent,
    trackSectionCreateContentScreenView, trackHubSectionContentLayoutSelectedEvent, trackSectionContentAddedEvent,
    trackSectionContentRemovedEvent, trackSectionContentSelectionSavedEvent, trackSectionEditedEvent, trackSectionCreatedEvent
  } = useHubAdoption();

  useEffect(() => {
    trackSectionCreateLayoutScreenView();
  }, [])

  useEffect(() => {
    if (!isRowItemUpdating) {
      if (createSection) {
        setRowItem(defaultRow);
        setPersistedRowItem(defaultRow);
        setSelectedEntity({ 0: [] })
        setPersistedSelectedEntities({ 0: [] })
      }
      else {
        const row = hubLayout?.rows[activeIndex].columns;

        let existingEntities = {};

        row.map((column, index) => {
          const items = sections[_.get(column, "section.id", "")]?.items || [];
          const isSmartFilter = column.section.smartFilter ? true : false;

          if (isSmartFilter) {
            setSelectedEntity({ ...selectedEntity, [index]: [] })
            setPersistedSelectedEntities({ ...persistedSelectedEntities, [index]: [] })
          }
          else {
            if (_.size(items) > 0) {
              existingEntities = {
                ...existingEntities,
                [index]: items
              }
            }
          }
        });
        setSelectedEntity(existingEntities);
        setPersistedSelectedEntities(existingEntities);
        setRowItem(hubLayout?.rows[activeIndex]?.columns);
        setPersistedRowItem(hubLayout?.rows[activeIndex]?.columns);

      }
    }

  }, [hubLayout, sections])


  useEffect(() => {
    if (!isHubUpdating && isHubUpdated) {
      handleUpdateFinish();
    }
  }, [isHubUpdating, isHubUpdated]);

  useEffect(() => {
    if (currentStep === 1) {
      trackSectionCreateContentScreenView();
    }
  }, [currentStep])

  const nextButtonText = useMemo(() => {
    if (currentStep === 1) {

      return "Save";
    }

    return "Next";
  }, [isRowItemUpdating, currentStep]);

  const handleConfirm = () => {
    goToNextStep(currentStep);
  };

  const handleCancelClick = () => {
    if (entityTreeCancelRef.current) {
      entityTreeCancelRef.current.handleEntityTreeCancel();
    }
  };

  const handleUpdateFinish = () => {
    const id = window.location.pathname.split("/hub/layout/")[1];
    dispatch(fetchHubLayout({ id }));
    setIsRowItemUpdating(false);
    setShowConfirmSlider(false); 

    const sectionNamesArray = _.map(rowItem, 'section.title');

    if (layoutPalette) {
      const eventPayload = {
        layout_palette: layoutPalette,
        layout_type: entityType,
        section_name: sectionNamesArray
      };
      createSection ? trackSectionCreatedEvent(eventPayload) : trackSectionEditedEvent(eventPayload);
    }
  }

  const handleSelection = (entity: Asset | Stream | Collection) => {

    const prevItems = selectedEntity[activeColumnIndex] || [];
    const exists = _.some(prevItems, { id: entity.id });
    if (exists) {
      const updatedItems = _.reject(prevItems, { id: entity.id });
      const updatedSelectedEntity = {
        ...selectedEntity,
        [activeColumnIndex]: updatedItems,
      }
      trackSectionContentRemovedEvent(entity.entityType as EntityType);
      setSelectedEntity(updatedSelectedEntity);
    }
    else {
      const updatedItems = [...prevItems, entity];
      const updatedSelectedEntity = {
        ...selectedEntity,
        [activeColumnIndex]: updatedItems
      }
      trackSectionContentAddedEvent(entity.entityType as EntityType);
      setSelectedEntity(updatedSelectedEntity);
    }
  };

  const updateHubSectionProps: IUseFetchProps = {
    action: updateHubSection,
    loading: isHubSectionUpdating,
    succeeded: isHubSectionUpdated,
    response: updatedHubSection,
    error: hubSectionError
  }

  const { handleFetch: updateHubSectionRow } = useFetch(updateHubSectionProps);

  const handleRowItemUpdate = () => {

    if(layoutPalette){
      trackSectionSaveConfirmClickedEvent({ layoutType: `${entityType}s`, layoutPalette });
    }

    const rowIndex = createSection ? activeIndex + 1 : activeIndex;
    const columnsWithSelectedEntites = rowItem?.map((column, index) => {
      const isSmartFilter = column.section.smartFilter ? true : false;
      const contents = isSmartFilter ? [] : selectedEntity[index];
      const contentIds = isSmartFilter ? [] : _.map(contents, (item) => _.get(item, "id"));

      return {
        ...column,
        section: {
          ...column.section,
          contents: contents,
          contentIds: contentIds,
          id: createSection ? "" : column.section.id
        },
      };
    });

    const updatedColumns = columnsWithSelectedEntites.map(obj => ({
      ...obj,
      section: _.omit(obj.section, 'tempId')
    }));

    setIsRowItemUpdating(true);

    const requestBody = {
      data: updatedColumns,
      rowIndex,
      create: createSection
    }
    updateHubSectionRow(requestBody).then((response) => {

      const updatedRows = [...hubLayout.rows];
      if (createSection) {
        updatedRows.splice(rowIndex, 0, response);
      } else {
        updatedRows[rowIndex] = response;
      }

      var hubLayoutRows = updatedRows.map((row, index) => {
        var columns = row.columns.map((column, columnIndex) => {
          if (index == rowIndex) {
            var updatedColumn = response.columns[columnIndex]
            return {
              section: {
                id: updatedColumn.section.id,
                title: _.get(updatedColumn, "section.title") || null,
                contentIds: _.get(updatedColumn, "section.smartFilter") == "RECOMMENDED_CATEGORIES" ? null : _.map(updatedColumn.items, 'id'),
                smartFilter: _.get(updatedColumn, "section.smartFilter") || null,
                entityType: _.get(updatedColumn, "section.smartFilter") == "RECOMMENDED_CATEGORIES" ? "ASSET" : _.toUpper(updatedColumn.section.entityType),
                layoutStyle: _.get(updatedColumn, "section.layoutStyle") || null,
                cardStyle: _.get(updatedColumn, "section.cardStyle") || null
              },
              width: updatedColumn.width
            }
          }
          var section = sections[_.get(column, "section.id", "")];
          if (section.items != null && section.items.length > 0)
            return {
              section: {
                id: column.section.id,
                title: _.get(column, "section.title") || null,
                contentIds: section.smartFilter == "RECOMMENDED_CATEGORIES" ? null : _.map(section.items, 'id'),
                smartFilter: section.smartFilter,
                entityType: section.smartFilter == "RECOMMENDED_CATEGORIES" ? "ASSET" : _.toUpper(section.items[0].entityType),
                layoutStyle: _.get(column, "section.layoutStyle") || null,
                cardStyle: _.get(column, "section.cardStyle") || null
              },
              width: column.width
            }
        });
        return {
          columns: columns
        }
      });

      var layout = {
        id: hubLayout.id,
        layoutName: hubLayout.layoutName,
        rows: hubLayoutRows,
        isCreate: false
      }

      dispatch(updateHubLayout({ layout }));

    }).catch((error) => {
      handleUpdateFinish();
    })
  }

  const entityType = useMemo(() => {
    const entityType = _.get(rowItem, '[0].section.entityType');
    const capitalizedEntityType = _.capitalize(entityType) as EntityType;
    return capitalizedEntityType;
  }, [rowItem])

  const goToNextStep = (step) => {
    if (step === 1) {
      if (layoutPalette) {
        trackSectionSaveClickedEvent({ layoutType: `${entityType}s`, layoutPalette });
      }
      setShowConfirmSlider(true);
    } else {
      const isExistingLayout = _.get(rowItem[0], "section.id") ? true : false;
      const isSmartFilter = rowItem[0]?.section?.smartFilter ? true : false;
      if (isExistingLayout && !isSmartFilter) {
        setShowEntityTree(true);
      }
      setCurrentStep(step + 1);

      if (layoutPalette) {
        trackHubSectionContentLayoutSelectedEvent({ layoutType: `${entityType}s`, layoutPalette });
      }

    }
  };

  const handleStepChange = (step: number) => {
    if (showEntityTree) return;
    setCurrentStep(step);
  };

  const renderBackButton = () => {
    let buttonTitle = "edit hub";

    if (currentStep === 1) {
      buttonTitle = "choose layout";
    }

    return (
      <div className={cx(styles.backButton, {
        [styles.disabled]: showEntityTree
      })} onClick={() => {
        if (showEntityTree) return;

        setShowEntityTree(false);
        if (currentStep == 0) {
          hideEditSection();
        }
        else {
          setCurrentStep(currentStep - 1)
        }
      }}>
        <i className="paperflite-left-arrow"></i>
        <span className={styles.title}>{buttonTitle}</span>
      </div>
    )
  };

  const updateRowItem = (field, value) => {

    switch (field) {
      case "rowItem":
        setRowItem(value);
        var entitiesObj = {}
        value.map((column, index) => {
          entitiesObj[index] = []
        });
        setSelectedEntity(entitiesObj);
        setIsTypeSelectionMode(true);
        break;
      case "smartFilter": {
        const updatedRow = rowItem.map((column, index) => {
          if (index === activeColumnIndex) {
            return {
              ...column,
              section: {
                ...column.section,
                smartFilter: value,
                tempId: _.get(column, "section.tempId", guid()),
                contents: [],
                contentIds: []
              }
            }
          }
          return column;
        });
        setSelectedEntity({ ...selectedEntity, [activeColumnIndex]: [] });
        setRowItem(updatedRow);
      }
        break;
      case "title": {
        const updatedRow = rowItem.map((column, index) => {
          if (index === activeColumnIndex) {
            return {
              ...column,
              section: {
                ...column.section,
                title: value
              }
            }
          }
          return column;
        });
        setRowItem(updatedRow);
      }
        break;
      default:
        break;
    }
  }

  const contextValues = {
    rowItem,
    updateRowItem,
    persistedRowItem: persistedRowItem,
    activeColumnIndex,
    persistedSelectedEntities,
    setActiveColumnIndex,
    selectedEntity,
    setSelectedEntity,
    showEntityTree,
    setShowEntityTree,
    createSection
  }

  const renderNextButton = useMemo(() => {

    if (showEntityTree || showConfirmSlider) { 
      return false;
    }

    if (currentStep === 0 || (currentStep === 1 && !isTypeSelectionMode)) {
      return true
    }
    return false
  }, [currentStep, isTypeSelectionMode, showEntityTree, showConfirmSlider])

  const isContentsAdded = useMemo(() => {

    const disable = currentStep !== 1 || rowItem.some((column, index) =>
      column.section.smartFilter || selectedEntity[index]?.length > 0
    ) || _.size(selectedEntity) > 0;

    return disable;

  }, [rowItem, selectedEntity, currentStep])

  const sectionNames = useMemo(() => {
    const namesArray = _.map(rowItem, 'section.title');
    return namesArray.join(" and ");
  }, [rowItem])

  return (
    <div className={styles.editHubContainer}>
      <EdithubContext.Provider value={contextValues}>
        <div className={styles.outerContainer}>
          {/* entity tree view */}
          <div className={cx(styles.entityTreeView, {
            [styles.renderEntityTreeView]: showEntityTree
          })}>
            <div className={styles.headerContainer}>
              <div className={styles.header}> manage content</div>
              <div className={styles.subText}>
              Manually add content, because sometimes you just need to take charge! </div>
            </div>
            <div className={styles.treeView}>
              <SelectEntity
                style={{}}
                width={350}
                onSelection={handleSelection}
                selectedEntity={selectedEntity[activeColumnIndex]}
              />

              <div className={styles.footerContainer}>

                <Button icon="paperflite-tick"
                  text={'Confirm'}
                  variant='primary'
                  disabled={isRowItemUpdating}
                  onClick={() => {
                    trackSectionContentSelectionSavedEvent(entityType);
                    setShowEntityTree(false)
                  }
                  }
                />
                <Button text="Cancel"
                  variant='secondary'
                  disabled={isRowItemUpdating}
                  onClick={() => {
                    handleCancelClick();
                  }}
                />

              </div>
            </div>

          </div>
        </div>

        <div className={cx(styles.innerContainer, {
          [styles.manualSelectionActive]: showEntityTree
        })}>
          <p>{renderBackButton()}</p>

          <Wizard.Pane
            className={cx(styles.wizard, {
              [styles.stepsDisabled]: showEntityTree
            })}
            currentStep={currentStep}
            gap={30}
            onStepChange={handleStepChange}
            wideView={true}
          >
            <Wizard.Step className={styles.wizardStep} name="Choose Layout"  completed={true}>
              <Layout setLayoutPalette={setLayoutPalette} />
            </Wizard.Step>
            <Wizard.Step name="Select Content" completed={true}>
              <SelectContent
                isTypeSelectionMode={isTypeSelectionMode}
                setIsTypeSelectionMode={setIsTypeSelectionMode}
                ref={entityTreeCancelRef} />
            </Wizard.Step>
          </Wizard.Pane>
          {renderNextButton && <div className={styles.sliderButtons}>
            <Button
              className={styles.confirmButton}
              icon={currentStep === 1 ? "paperflite-tick" : "paperflite-right-arrow"}
              text={nextButtonText}
              variant="primary"
              disabled={isRowItemUpdating || !isContentsAdded}
              onClick={handleConfirm}
            />
            <Button
              text="Cancel"
              variant="secondary"
              disabled={isRowItemUpdating}
              onClick={() => {
                hideEditSection()
              }}
            />
          </div>}

          <Slider.Dialog
            width="300"
            show={showConfirmSlider}
            onClose={() => {
              setShowConfirmSlider(false);
            }}
          >
            <Slider.Header
              title="confirm changes"
              message={sectionNames} />
            <Slider.Body className={styles.sliderBody}>
              <img
                className={styles.sliderImage}
                src={Peek}
              />
              Changes made to this section will land on every user's screen. Still want to proceed?
            </Slider.Body>

            <Slider.Actions>
              <Button variant="primary" icon='paperflite-tick' text={isRowItemUpdating ? "Creating..." : "Confirm"} disabled={isRowItemUpdating} onClick={handleRowItemUpdate}></Button>
              <Button variant="secondary" text="Cancel" disabled={isRowItemUpdating} onClick={() => setShowConfirmSlider(false)}></Button>
            </Slider.Actions>
          </Slider.Dialog>

        </div>
      </EdithubContext.Provider>
    </div>
  );
};

export default EditHub;
